import { Link } from 'react-router-dom';
import { Calendar, Award, Sparkles, ShieldCheck } from 'lucide-react';
import SEO from '../../components/SEO';

export default function OmDanshakerEn() {
  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: 'About Danshaker',
    description: 'The story behind the Danshaker sorting machine, developed by Rasmus Gangsted-Rasmussen since 2019.',
    url: 'https://danshaker.dk/en/about-danshaker',
  };

  return (
    <div className="relative min-h-screen bg-slate-50 text-slate-900 antialiased">
      <SEO
        title="About Danshaker - The Story Behind the Machine"
        description="Read the story of Danshaker, created in Randers, Denmark by Rasmus Gangsted-Rasmussen. Quality engineering built to save time and reduce physical strain in horse stables."
        path="/en/about-danshaker"
        lang="en"
        schema={aboutSchema}
      />

      {/* Background gradients */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(14,103,162,0.05),transparent_60%)]" />

      {/* Main Content */}
      <section className="mx-auto max-w-7xl px-4 pt-16 pb-20 md:px-8 md:pt-24">
        <div className="grid gap-12 lg:grid-cols-12 lg:items-center">
          {/* Left Column: Info */}
          <div className="lg:col-span-7 space-y-6">
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 rounded-full bg-[#0E67A2]/10 px-4 py-1.5 text-xs font-bold text-[#0E67A2]">
                <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
                <span>The story behind the original machine</span>
              </div>
              <h1 className="text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl md:text-6xl">
                About Danshaker
              </h1>
            </div>

            <div className="space-y-4 text-slate-700 leading-relaxed text-base md:text-lg">
              <p>
                My name is Rasmus Gangsted-Rasmussen, and I began developing the Danshaker back in 2019. I did so driven by a strong desire to alleviate the heavy, straining mucking work for my employees and boarding clients at my previous equestrian facility, Stald Fredenshøj in Randers, Denmark. Furthermore, I wanted to see if it was possible to save more time and reduce bedding expenses.
              </p>
              <p>
                Danshaker’s journey towards becoming the refined quality product it is today has been extensive and certainly not without obstacles. But a combination of skilled manufacturing partners and my own determination to succeed has been decisive in reaching this milestone.
              </p>
              <p>
                What motivates me most of all is seeing how much improved quality of life many of our customers enjoy after acquiring a Danshaker. The machine does the heavy vibrating and sorting work, sparing owners and stable hands from needless physical strain in their everyday routines.
              </p>
            </div>

            {/* Core Values / Bullet points */}
            <div className="grid gap-4 sm:grid-cols-2 pt-4">
              <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <ShieldCheck className="h-8 w-8 text-[#0E67A2] shrink-0" />
                <div>
                  <h2 className="font-bold text-slate-900 text-sm">Danish Quality</h2>
                  <p className="text-xs text-slate-500 font-medium">Developed & built in Denmark</p>
                </div>
              </div>

              <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <Award className="h-8 w-8 text-[#0E67A2] shrink-0" />
                <div>
                  <h2 className="font-bold text-slate-900 text-sm">Equestrian Focus</h2>
                  <p className="text-xs text-slate-500 font-medium">Built by horse lovers for horse lovers</p>
                </div>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4 pt-4">
              <Link
                to="/en/free-demo"
                className="flex items-center gap-2 rounded-xl bg-[#0E67A2] px-6 py-3.5 font-bold text-white shadow-lg shadow-[#0E67A2]/10 transition hover:bg-[#0E67A2]/90"
              >
                <Calendar className="h-5 w-5" aria-hidden="true" />
                Book free demo
              </Link>
              <Link
                to="/en/product"
                className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-6 py-3.5 font-bold text-slate-700 shadow-sm transition hover:bg-slate-50"
              >
                Learn about the machine
              </Link>
            </div>
          </div>

          {/* Right Column: Image */}
          <div className="lg:col-span-5">
            <div className="relative mx-auto max-w-md lg:max-w-none">
              <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white p-3 shadow-2xl">
                <img
                  src="https://assets.auxodev.dk/assets/e0cd175c-a113-44c2-9ab9-d86f3c298af2/88965ac8-a30d-41c2-b3d4-878dd054d8f1.jpg"
                  alt="Rasmus Gangsted-Rasmussen with the Danshaker"
                  width="600"
                  height="800"
                  fetchPriority="high"
                  decoding="async"
                  className="aspect-4/5 w-full rounded-2xl object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 hidden sm:block rounded-2xl border border-slate-200 bg-white p-4 shadow-xl">
                <p className="text-xs font-bold text-[#0E67A2] uppercase tracking-wider">Founder & Developer</p>
                <p className="text-sm font-extrabold text-slate-900">Rasmus Gangsted-Rasmussen</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
